import Image from 'next/image';
import cn from 'clsx';
import {useEffect, useRef, useState} from 'react';
import {useMediaForOneSku} from '@components/product/global/tabs/MediaPhotos/MediaHelpers';
import {UploadMediaForProductTab} from '@components/product/global/UserMediaUpload';
import {SpinnerV3} from '@components/widgets/Spinners/LoadingCircle';
import {MdOutlineRotateLeft, MdOutlineRotateRight} from '@assets/react-icons/md';
import {useIsMobile} from '@helpers/hooks/useWindow';

export const AdminPanelUserSubmittedMedia = ({sku, skuList, openLightbox}) => {
  const isMobile = useIsMobile();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('approved');
  const [selectedImages, setSelectedImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [topApproveButtonVisible, setTopApproveButtonVisible] = useState(false);
  const isInitialLoad = useRef(true);

  const {images, mutateMedia, updateStatuses, rotateImage} = useMediaForOneSku(skuList);

  useEffect(() => {
    setSelectedImages([]);
    switch (activeTab) {
      case 'pendingReview':
        setDisplayedImages(images?.filter((x) => x?.status === 'pending_approval') || []);
        break;
      case 'approved':
        setDisplayedImages(images?.filter((x) => x?.status === 'approved') || []);
        break;
      case 'rejected':
        setDisplayedImages(images?.filter((x) => x?.status === 'rejected') || []);
        break;
      default:
        setDisplayedImages([]);
    }
  }, [activeTab, images]);

  useEffect(() => {
    if (activeTab === 'pendingReview' && isInitialLoad.current) {
      setTopApproveButtonVisible(true);
      isInitialLoad.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    const hasPendingReviewImages = images?.some((image) => image.status === 'pending_approval');
    if (isInitialLoad.current && hasPendingReviewImages) {
      setActiveTab('pendingReview');
      isInitialLoad.current = false;
    }
  }, [images]);
  // console.log('[AdminPanelUserSubmittedMedia] activeTab:', activeTab);

  const toggleImageSelection = (imageId) => {
    setSelectedImages((prevSelectedImages) =>
      prevSelectedImages.includes(imageId)
        ? prevSelectedImages.filter((id) => id !== imageId)
        : [...prevSelectedImages, imageId]
    );
  };

  const onClick = async (actionType, imageIds) => {
    setSubmitting(true);
    switch (actionType) {
      case 'approved':
        await updateStatuses('approved', imageIds);
        break;
      case 'rejected':
        await updateStatuses('rejected', imageIds);
        break;
      default:
        console.log('Action type not recognized');
    }
    setSubmitting(false);
    setSelectedImages([]);
  };

  const handleRotateImage = async (rotateAngle) => {
    setSubmitting(true);

    const imageUrl = displayedImages?.find((x) => x.id === selectedImages[0])?.image_url;
    if (imageUrl) {
      await rotateImage(imageUrl, rotateAngle);
    } else {
      console.log('No image url found');
    }

    setSubmitting(false);
  };

  const onBatchActionClick = async (actionType) => {
    if (selectedImages?.length > 0) {
      await onClick(actionType, selectedImages);
    } else {
      console.log('No images selected for batch action');
    }
  };

  const onClickSelectAllImages = () => {
    setSelectedImages(allPendingImageIds);
    setTopApproveButtonVisible(true);
    if (selectedImages?.length && selectedImages?.length > 0) {
      setSelectedImages([]);
      setTopApproveButtonVisible(false);
    }
  };

  const pendingImages = images?.filter((x) => x?.status === 'pending_approval') || [];
  const allPendingImageIds = pendingImages?.map((x) => x?.id) || [];

  const hasSelectedImages = selectedImages?.length > 0;
  const approveButtonDisabled = !hasSelectedImages || submitting;
  const rejectButtonDisabled = !hasSelectedImages || submitting;

  const showSelectAllButton = activeTab === 'pendingReview';
  const showTopApproveButton =
    (topApproveButtonVisible || hasSelectedImages) && activeTab === 'pendingReview';
  const showApproveButton = activeTab === 'pendingReview' || activeTab === 'rejected';
  const showRejectButton = activeTab === 'pendingReview' || activeTab === 'approved';
  const showRotateButtons = selectedImages?.length === 1;

  let parentStyle = {};
  if (isMobile) {
    parentStyle = {
      marginTop: '3px'
    };
  }

  let style = {
    marginTop: '-5px',
    gap: '15px'
  };
  if (isMobile) {
    style = {
      marginTop: '-2px'
    };
  }

  return (
    <div className="md:p-1 relative" style={parentStyle}>
      <div
        className={
          'md:mb-1.5 border-b border-b-neutral-500 md:border-b-neutral-200 sticky top-0 md:bg-white'
        }
        style={style}>
        {['approved', 'pendingReview', 'rejected', 'upload'].map((tab) => {
          const isPendingReview = tab === 'pendingReview';
          const hasPendingImages = pendingImages?.length > 0;

          const showYellowBackground = isPendingReview && hasPendingImages;
          const isActive = activeTab === tab;

          return (
            <button
              key={tab}
              className={cn(
                'text-white p-1 mr-3 cursor-pointer text-base border-b-3 border-b-transparent',
                {
                  'border-b-[#3182CE] md:text-[#2f3846]': isActive,
                  'text-white md:text-[#06192a]': !isActive,
                  'bg-[#ADAA7A] md:bg-[#C3DDAF]': showYellowBackground
                }
              )}
              onClick={() => setActiveTab(tab)}>
              {tab?.charAt(0).toUpperCase() +
                tab
                  ?.slice(1)
                  ?.replace(/([A-Z])/g, ' $1')
                  ?.trim()}
            </button>
          );
        })}
      </div>

      {activeTab === 'upload' && (
        <div style={tabContentStyle}>
          <UploadMediaForProductTab sku={sku} mutateMedia={mutateMedia} showUpload={true}/>
        </div>
      )}

      {['pendingReview', 'approved', 'rejected'].includes(activeTab) && (
        <div style={tabContentStyle}>
          {displayedImages?.length === 0 && <div className="pt-3">No images here yet</div>}
          {displayedImages?.length > 0 && (
            <>
              <div className={'flex gap-3 justify-between w-full px-2 mt-2'}>
                {showSelectAllButton && (
                  <button style={buttonStyle('#497072')} onClick={onClickSelectAllImages}>
                    Select All
                  </button>
                )}

                {showTopApproveButton && (
                  <button
                    style={buttonStyle('#497072')}
                    onClick={() => onBatchActionClick('approved')}>
                    Approve Selected
                  </button>
                )}
              </div>
              <div style={imageGridStyle}>
                {displayedImages?.map((image, index) => {
                  console.log("[AdminPanelUserSubmittedMedia] image:", image);
                  const isSelected = selectedImages?.includes(image.id);
                  const width = image?.meta_data?.width || 1600;
                  const height = image?.meta_data?.height || 1200;

                  return (
                    <div
                      key={`user-submitted-image_${index + 1}`}
                      className={`flex flex-col items-center cursor-pointer mb-2`}>
                      <Image
                        src={image?.image_url}
                        alt={image?.alt_text || `Customer Submitted Image #${index + 1}`}
                        style={imageStyle}
                        width={width}
                        height={height}
                        className={`border-2 ${isSelected ? 'border-sky-800' : 'border-transparent'}`}
                        onClick={() => openLightbox(index, displayedImages)}
                        sizes={'200px'}
                      />
                      <label className={'w-full flex justify-center'}>
                        <input
                          style={{marginTop: '10px', width: '16px', height: '16px'}}
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleImageSelection(image.id)}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>

              <div
                className="justify-between flex gap-2.5 mt-16 w-full sticky bottom-0 bg-[#26292b] md:bg-white border-t border-t-neutral-200 pt-2">
                <div className={'flex gap-2'}>
                  {showApproveButton && (
                    <button
                      style={buttonStyle('#38A169', approveButtonDisabled)}
                      disabled={approveButtonDisabled}
                      onClick={() => onBatchActionClick('approved')}>
                      Approve
                    </button>
                  )}
                  {showRejectButton && (
                    <button
                      style={buttonStyle('#E53E3E', rejectButtonDisabled)}
                      disabled={rejectButtonDisabled}
                      onClick={() => onBatchActionClick('rejected')}>
                      Reject
                    </button>
                  )}
                </div>

                {showRotateButtons && (
                  <div className={'flex gap-2'}>
                    <button
                      style={buttonStyle('#222')}
                      onClick={() => handleRotateImage(-90)}
                      title={'Rotate Image 90 Degrees Counterclockwise'}>
                      <MdOutlineRotateLeft/>
                    </button>
                    <button
                      style={buttonStyle('#222')}
                      onClick={() => handleRotateImage(90)}
                      title={'Rotate Image 90 Degrees Clockwise'}>
                      <MdOutlineRotateRight/>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {submitting && (
        <div
          className={
            'absolute bg-neutral-100 bg-opacity-75 w-full h-full top-0 left-0 flex flex-col justify-center items-center'
          }>
          <SpinnerV3/>
        </div>
      )}
    </div>
  );
};

const tabContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'center',
  width: '100%'
};

const buttonStyle = (backgroundColor, disabled) => ({
  backgroundColor: backgroundColor,
  color: '#ffffff',
  padding: '5px',
  borderRadius: '4px',
  border: 'none',
  cursor: disabled ? 'default' : 'pointer',
  fontFamily: 'Arial, sans-serif',
  fontSize: '13px',
  transition: 'background-color 0.3s',
  opacity: disabled ? 0.5 : 1
});

const imageGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 31%)',
  gridTemplateRows: 'repeat(125px)',
  justifyContent: 'space-between',
  width: '100%',
  marginTop: '3px',
  border: '1px solid rgba(229, 229, 229, 1)',
  borderRadius: '8px',
  padding: '8px 8px'
};

const imageStyle = {
  width: '100%',
  height: '125px',
  objectFit: 'cover',
  borderRadius: '6px'
};
